import { motion } from 'framer-motion';
import { useState } from 'react';
import axios from 'axios';
import { Handshake, Heart, Globe, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Helmet } from 'react-helmet-async';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Collaborazioni = () => {
  const [formData, setFormData] = useState({ nome: '', email: '', messaggio: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Messaggio inviato con successo! Ti risponderò presto.');
      setFormData({ nome: '', email: '', messaggio: '' });
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const values = [
    {
      icon: Heart,
      title: 'Autenticità',
      description: 'Collaboriamo solo con brand e strutture che rispecchiano i nostri valori.'
    },
    {
      icon: Globe,
      title: 'Sostenibilità',
      description: 'Promuoviamo esperienze che rispettano l\'ambiente e le comunità locali.'
    },
    {
      icon: Handshake,
      title: 'Trasparenza',
      description: 'Ogni collaborazione è sempre dichiarata chiaramente alla community.'
    }
  ];

  return (
    <div data-testid="collaborazioni-page">
      <Helmet>
        <title>Collaborazioni | Travel Creator Famiglia con Bambini — Chichi in Viaggio</title>
        <meta name="description" content="Collabora con Chichi in Viaggio: travel creator specializzati in viaggi lenti con bambini, 40k follower su Instagram. Brand, enti turistici e strutture." />
      </Helmet>
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-6"
            data-testid="collaborazioni-title"
          >
            Collaborazioni
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base md:text-lg leading-relaxed text-foreground/80"
            data-testid="collaborazioni-subtitle"
          >
            Collaboriamo con brand, hotel e strutture che condividono la nostra visione di viaggio
            autentico, slow e rispettoso. Se pensi che possiamo lavorare insieme, parliamone.
          </motion.p>
        </div>

        <div className="max-w-7xl mx-auto mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-serif text-3xl md:text-4xl tracking-tight mb-12 text-center"
            data-testid="values-section-title"
          >
            I nostri principi
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="text-primary" size={24} />
                </div>
                <h3 className="font-serif text-2xl mb-3">{value.title}</h3>
                <p className="text-foreground/70 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-muted p-8 md:p-12 rounded-2xl"
            data-testid="ideal-partners-section"
          >
            <h2 className="font-serif text-3xl mb-6">Con chi lavoriamo</h2>
            <div className="space-y-4 text-foreground/80 leading-relaxed">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-primary flex-shrink-0 mt-1" size={20} />
                <p>
                  <strong>Strutture ricettive</strong> che promuovono il turismo sostenibile e
                  l'esperienza locale autentica.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-primary flex-shrink-0 mt-1" size={20} />
                <p>
                  <strong>Brand travel</strong> che valorizzano la famiglia e il viaggio
                  consapevole.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-primary flex-shrink-0 mt-1" size={20} />
                <p>
                  <strong>Destinazioni</strong> che vogliono promuoversi attraverso uno
                  storytelling autentico.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-primary flex-shrink-0 mt-1" size={20} />
                <p>
                  <strong>Prodotti per bambini</strong> pensati per il viaggio e l'esplorazione.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6 text-center" data-testid="contact-form-title">
              Contattaci
            </h2>
            <p className="text-center text-foreground/70 mb-8">
              Compila il form qui sotto e ti risponderemo al più presto.
            </p>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="collaborazioni-contact-form">
              <div>
                <Input
                  type="text"
                  placeholder="Il tuo nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  className="w-full"
                  data-testid="contact-form-name"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="La tua email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full"
                  data-testid="contact-form-email"
                />
              </div>
              <div>
                <Textarea
                  placeholder="Raccontami del tuo progetto o della collaborazione che hai in mente..."
                  value={formData.messaggio}
                  onChange={(e) => setFormData({ ...formData, messaggio: e.target.value })}
                  required
                  rows={6}
                  className="w-full resize-none"
                  data-testid="contact-form-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full py-6 text-lg"
                data-testid="contact-form-submit"
              >
                {loading ? 'Invio in corso...' : 'Invia messaggio'}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary text-primary-foreground" data-testid="instagram-cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading font-light text-3xl mb-4">Seguici su Instagram</h3>
          <p className="text-primary-foreground/80 leading-relaxed mb-6">
            Per conoscere meglio il nostro lavoro, visita il nostro profilo Instagram.
          </p>
          <a
            href="https://instagram.com/chichi_inviaggio"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-lg font-medium text-primary-foreground hover:text-white transition-colors"
            data-testid="instagram-link"
          >
            @chichi_inviaggio →
          </a>
        </div>
      </section>
    </div>
  );
};

export default Collaborazioni;